"use client";

import { useActionState, useCallback, useId } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, AlertCircle, CheckCircle, ChevronDown, Loader2 } from "lucide-react";
import { submitContact } from "@/app/actions/contact";
import {
  CONTACT_COMMUNICATION_CONSENT_TEXT,
  CONTACT_PROCESSING_CONSENT_TEXT,
  CONTACT_REASONS,
  CONTACT_RECAPTCHA_ACTION,
  GENERIC_FORM_ERROR,
  inputClass,
  labelClass,
  submitButtonClass,
  type FormState,
} from "@/lib/forms";
import { RECAPTCHA_READY, getRecaptchaToken, preloadRecaptcha } from "@/lib/recaptcha-client";

const initialState: FormState = { status: "idle" };

export function ContactForm() {
  const id = useId();
  const router = useRouter();

  const submit = useCallback(
    async (prev: FormState, formData: FormData): Promise<FormState> => {
      if (!RECAPTCHA_READY) {
        return {
          status: "error",
          message: "The form is unavailable right now. Please email info@cloudbreach.io.",
        };
      }
      try {
        formData.set("recaptchaToken", await getRecaptchaToken(CONTACT_RECAPTCHA_ACTION));
      } catch {
        return {
          status: "error",
          message: "We couldn't verify your browser. Please reload the page and try again.",
        };
      }
      formData.set("pageUri", window.location.href);
      formData.set("pageName", document.title);
      try {
        const result = await submitContact(prev, formData);
        // The thank-you page is the conversion target the old HubSpot embed
        // redirected to, so analytics goals keep working unchanged.
        if (result.status === "success") router.push("/thank-you");
        return result;
      } catch {
        return { status: "error", message: GENERIC_FORM_ERROR };
      }
    },
    [router],
  );

  const [state, formAction, pending] = useActionState(submit, initialState);

  if (state.status === "success") {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-4" role="status" aria-live="polite">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cb-success/10 border border-cb-success/20">
          <CheckCircle className="h-6 w-6 text-cb-success" aria-hidden="true" />
        </div>
        <h3 className="text-base font-semibold text-cb-text">Message received</h3>
        <p className="text-sm text-cb-muted max-w-xs leading-relaxed">
          Thanks for reaching out. We aim to reply within one business day.
        </p>
      </div>
    );
  }

  const errorId = `${id}-error`;
  const describedBy = state.status === "error" ? errorId : undefined;

  return (
    <form
      action={formAction}
      onFocus={RECAPTCHA_READY ? preloadRecaptcha : undefined}
      className="relative space-y-6"
    >
      {!RECAPTCHA_READY && (
        <div className="flex items-start gap-2 rounded-lg border border-cb-danger/20 bg-cb-danger/5 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-cb-danger shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-cb-danger leading-relaxed">
            The form cannot verify your browser right now. Please email{" "}
            <a href="mailto:info@cloudbreach.io" className="underline font-medium">
              info@cloudbreach.io
            </a>
            .
          </p>
        </div>
      )}

      <div>
        <label htmlFor={`${id}-reason`} className={labelClass}>
          Subject <span className="text-cb-danger" aria-hidden="true">*</span>
        </label>
        <div className="relative">
          <select
            id={`${id}-reason`}
            name="reason"
            required
            defaultValue=""
            className={`${inputClass} appearance-none pr-11 cursor-pointer`}
            aria-describedby={describedBy}
          >
            <option value="" disabled>
              Select a subject
            </option>
            {CONTACT_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cb-muted-2"
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor={`${id}-email`} className={labelClass}>
            Email <span className="text-cb-danger" aria-hidden="true">*</span>
          </label>
          <input
            id={`${id}-email`}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            maxLength={254}
            placeholder="you@company.com"
            className={inputClass}
            aria-describedby={describedBy}
            aria-invalid={state.status === "error" || undefined}
          />
        </div>
        <div>
          <label htmlFor={`${id}-firstname`} className={labelClass}>
            Name <span className="normal-case tracking-normal font-normal text-cb-muted-2">(optional)</span>
          </label>
          <input
            id={`${id}-firstname`}
            name="firstname"
            type="text"
            autoComplete="given-name"
            maxLength={100}
            placeholder="Your name"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${id}-message`} className={labelClass}>
          Message <span className="text-cb-danger" aria-hidden="true">*</span>
        </label>
        <textarea
          id={`${id}-message`}
          name="message"
          required
          rows={6}
          maxLength={5000}
          placeholder="How can we help?"
          className={`${inputClass} resize-y min-h-32`}
          aria-describedby={describedBy}
        />
      </div>

      {/* Honeypot: visually removed and skipped by assistive tech; bots that
          fill every field trip the server-side check. */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={`${id}-website`}>Website</label>
        <input id={`${id}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="space-y-3">
        <label htmlFor={`${id}-consent`} className="flex items-start gap-3 cursor-pointer min-h-11">
          <input
            id={`${id}-consent`}
            name="consent"
            type="checkbox"
            required
            className="mt-1 h-4 w-4 shrink-0 rounded border-cb-border accent-cb-violet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
          />
          <span className="text-sm text-cb-muted leading-relaxed">
            {CONTACT_COMMUNICATION_CONSENT_TEXT} Check our{" "}
            <a href="/privacy" target="_blank" rel="noopener" className="underline hover:text-cb-text">
              Privacy Policy
            </a>
            .
          </span>
        </label>
        <p className="text-xs text-cb-muted-2 leading-relaxed">{CONTACT_PROCESSING_CONSENT_TEXT}</p>
      </div>

      {state.status === "error" && (
        <div
          id={errorId}
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-cb-danger/20 bg-cb-danger/5 px-4 py-3"
        >
          <AlertCircle className="h-4 w-4 text-cb-danger shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-cb-danger leading-relaxed">{state.message}</p>
        </div>
      )}

      <button type="submit" disabled={pending || !RECAPTCHA_READY} className={submitButtonClass}>
        {pending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending
          </>
        ) : (
          <>
            Send message
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </>
        )}
      </button>

      <p className="text-xs text-cb-muted-2 leading-relaxed">
        Protected by reCAPTCHA. Google&apos;s{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-cb-muted">
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-cb-muted">
          Terms of Service
        </a>{" "}
        apply.
      </p>
    </form>
  );
}
